import { toast } from "react-toastify";
import { useState, useEffect, useMemo } from "react";
import { useStoreWrapper } from "../../../../../store/store";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import {
  upperCaseReg,
  specialCharsReg,
  numberReg,
  PERMISSION_LIST,
} from "../../../../../shared/sharedConstants";
import useAuth from "../../../../Auth/hooks/useAuth";
import Modal from "../../../../../components/Modal";
import { BigBoldText } from "../../../dashboard/home/home.style";
import { Container } from "../../components/logIncident/logIncident.style";
import ActionBtnU from "../../components/ActionBtnU";
import FormErrors from "../../../../../components/FormErrors";
import { useFormik } from "formik";
import {
  Description,
  FormContainer,
  Header,
  Title,
  UpdatePasswordWrapper,
  UpdateButtonContainer,
  UpdateButton,
} from "../../../settings/UpdatePassword/updatePassword.style";

import { usePermission } from "../../../../../hooks/usePermission";
import { getUserDetails } from "../../../../../api/user";
import { useQuery } from "@tanstack/react-query";

const UserPassword = (props: any) => {
  const { resetPasswordForUser } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isC_PasswordVisible, setIsC_PasswordVisible] = useState(false);

  const { selectedUser } = useStoreWrapper((state) => ({
    selectedUser: state.selectedUser,
  }));

  const { userId } = selectedUser;

  const { isPermitted } = usePermission();

  const { data: targetDetails } = useQuery(
    ["userDetails", "password-gate", userId],
    () => getUserDetails(userId),
    { enabled: !!userId }
  );

  const targetFacilityIds: string[] = useMemo(() => {
    const facilities = targetDetails?.data?.data?.facilities;
    if (!Array.isArray(facilities)) return [];
    return facilities
      .map((facility: any) => facility?.facilityId)
      .filter(Boolean);
  }, [targetDetails]);

  /*
   * Same question the server asks (MrWebApp_AdminPasswordController.mayResetPasswordFor):
   * does the caller hold Request Password Reset on a facility the target is assigned to.
   * The two must agree. False while the target's facilities load.
   */
  const allowResetPassword = useMemo(
    () =>
      targetFacilityIds.some((facilityId) =>
        isPermitted(PERMISSION_LIST.REQUEST_PASSWORD_RESET, facilityId)
      ),
    [targetFacilityIds, isPermitted]
  );

  const { isLoading, mutateAsync: requestPasswordResetForUser } =
    resetPasswordForUser;
  const { handleChange, handleBlur, errors, values, handleSubmit } = useFormik({
    initialValues: {
      password: "",
      password__c: "",
    },
    onSubmit: async (values) => {
      try {
        await requestPasswordResetForUser({
          salesforceContactId: userId,
          newPassword: values.password,
        });
        toast.success("User Password reset successful");
        setIsModalOpen(false);
      } catch (error: any) {
        // Show the server's message when present; a refused password says which rule failed.
        const fromServer = error?.response?.data?.error;
        toast.error(
          typeof fromServer === "string" && fromServer.length > 0
            ? fromServer
            : "Error reseting user password"
        );
      }
    },
  });

  const [passwordCriteria, setPasswordCriteria] = useState({
    isUpperCase: false,
    isNumber: false,
    isSpecialChars: false,
    isEmpty: false,
    isEightChars: false,
  });

  useEffect(() => {
    upperCaseReg.test(values.password)
      ? setPasswordCriteria((prevState) => ({
          ...prevState,
          isUpperCase: true,
        }))
      : setPasswordCriteria((prevState) => ({
          ...prevState,
          isUpperCase: false,
        }));

    specialCharsReg.test(values.password)
      ? setPasswordCriteria((prevState) => ({
          ...prevState,
          isSpecialChars: true,
        }))
      : setPasswordCriteria((prevState) => ({
          ...prevState,
          isSpecialChars: false,
        }));

    numberReg.test(values.password)
      ? setPasswordCriteria((prevState) => ({
          ...prevState,
          isNumber: true,
        }))
      : setPasswordCriteria((prevState) => ({
          ...prevState,
          isNumber: false,
        }));

    values.password !== ""
      ? setPasswordCriteria((prevState) => ({ ...prevState, isEmpty: true }))
      : setPasswordCriteria((prevState) => ({
          ...prevState,
          isEmpty: false,
        }));

    values.password.length >= 8
      ? setPasswordCriteria((prevState) => ({
          ...prevState,
          isEightChars: true,
        }))
      : setPasswordCriteria((prevState) => ({
          ...prevState,
          isEightChars: false,
        }));
  }, [values.password]);

  const isBtnDiasbaled = () => {
    const disableBtn =
      isLoading ||
      values.password !== values.password__c ||
      values.password !== values.password__c ||
      !passwordCriteria.isEmpty ||
      !passwordCriteria.isUpperCase ||
      !passwordCriteria.isNumber ||
      !passwordCriteria.isSpecialChars ||
      !passwordCriteria.isEightChars;
    return disableBtn;
  };

  return (
    <>
      <Modal open={isModalOpen}>
        <Container>
          <div className="mb-7 flex">
            <button
              onClick={() => setIsModalOpen(false)}
              className="cursor-pointer border-0 bg-transparent text-4xl font-medium"
            >
              &times;
            </button>
            <div className="mt-3 flex-1 text-center">
              <BigBoldText>Reset User Password</BigBoldText>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div>
              <div className="mb-3">
                <div className="mb-1 font-semibold">
                  New Password <span className="ml-1 text-red-500">*</span>{" "}
                </div>
                <div className="flex items-center rounded border border-solid border-gray-400 pr-3">
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    name="password"
                    onChange={handleChange}
                    value={values.password}
                    onBlur={handleBlur}
                    className="h-12 w-full border-none px-5 focus:outline-none"
                  />
                  {isPasswordVisible ? (
                    <AiFillEyeInvisible
                      onClick={() => setIsPasswordVisible(false)}
                      size={23}
                      color="#B8BCCA"
                      className="cursor-pointer"
                    />
                  ) : (
                    <AiFillEye
                      onClick={() => setIsPasswordVisible(true)}
                      size={23}
                      color="#B8BCCA"
                      className="cursor-pointer"
                    />
                  )}
                </div>
              </div>
              <div className="mb-3">
                <div className="mb-1 font-semibold">
                  Reenter Password <span className="ml-1 text-red-500">*</span>
                </div>
                <div className="flex items-center rounded border border-solid border-gray-400 pr-3">
                  <input
                    type={isC_PasswordVisible ? "text" : "password"}
                    value={values.password__c}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    name="password__c"
                    className="h-12 w-full border-none px-5 focus:outline-none"
                  />
                  {isC_PasswordVisible ? (
                    <AiFillEyeInvisible
                      onClick={() => setIsC_PasswordVisible(false)}
                      size={23}
                      color="#B8BCCA"
                      className="cursor-pointer"
                    />
                  ) : (
                    <AiFillEye
                      onClick={() => setIsC_PasswordVisible(true)}
                      size={23}
                      color="#B8BCCA"
                      className="cursor-pointer"
                    />
                  )}
                </div>
              </div>
            </div>

            <FormErrors
              empty={passwordCriteria.isEmpty}
              upperCase={passwordCriteria.isUpperCase}
              number={passwordCriteria.isNumber}
              specialChars={passwordCriteria.isSpecialChars}
              eightChars={passwordCriteria.isEightChars}
              showMatch
              values={values}
              errors={errors}
              pwToCheck={"confirmPassword"}
            />

            <div className="mt-4 flex justify-end">
              <ActionBtnU
                purpleBg
                white_color
                type="submit"
                disabled={isBtnDiasbaled() || isLoading}
              >
                {isLoading ? "Updating..." : "Update"}
              </ActionBtnU>
            </div>
          </form>
        </Container>
      </Modal>

      <UpdatePasswordWrapper>
        <Header>
          <Title>Password</Title>
          <Description>
            Set a new password for this user.
          </Description>
        </Header>
        <FormContainer>
          <UpdateButtonContainer>
            <UpdateButton
              onClick={() => setIsModalOpen(true)}
              disabled={isLoading || !allowResetPassword}
            >
              Update Password
            </UpdateButton>
          </UpdateButtonContainer>
        </FormContainer>
      </UpdatePasswordWrapper>
    </>
  );
};
export default UserPassword;
